import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query'
import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  OpalApiResponseDocument,
  OpalApiRequestParams,
  views,
} from '../../../../v3'
import { globalQueryKeys } from '../../queryKeys'

type ResponseDocument = OpalApiResponseDocument<'ReadViewsV3', 200>
type NonPageQueryParams = Omit<
  OpalApiRequestParams<'ReadViewsV3'>['query'],
  'page'
>

type GetPlanArgs<T = ResponseDocument> = NonPageQueryParams & {
  limit?: number
  select?: (document?: InfiniteData<ResponseDocument>) => T
  workspaceId: string
}

const LIMIT = 100

type FetchPlansParams = NonPageQueryParams & {
  page: number
  limit: number
  workspaceId?: string
}

const fetchPlans = ({ filter, page, limit, include }: FetchPlansParams) =>
  views.readCollection({
    params: {
      query: {
        filter,
        include,
        page: { offset: page * limit, limit },
      },
    },
  })

export const usePaginatedViews = <T = ResponseDocument>({
  filter,
  include,
  limit = LIMIT,
  select,
  workspaceId,
}: GetPlanArgs<T>) => {
  const [initiatedAllRequests, setInitiatedAllRequests] = useState(false)

  const shouldFetchNextPage = useCallback(
    (document: InfiniteData<ResponseDocument>) => {
      const offset = document.pages.length * limit
      if (!document.pages[0].meta) return false
      if (document.pages[0].meta.total > offset) {
        return true
      }

      setInitiatedAllRequests(true)
      return false
    },
    [limit]
  )

  const { data, isFetchingNextPage, isFetching, fetchNextPage } =
    useInfiniteQuery({
      refetchInterval: false,
      queryKey: globalQueryKeys.viewsByWorkspace(workspaceId, {
        filter,
        include,
        page: { limit },
      }),
      queryFn: ({ pageParam }) =>
        fetchPlans({
          page: pageParam ?? 0,
          filter,
          include,
          workspaceId,
          limit,
        }),
      getNextPageParam: (_lastPage, pages) => pages.length,
    })

  const plans = useMemo(() => select?.(data), [select, data])
  const resultsObject = useMemo(
    () => ({
      fetchingComplete: initiatedAllRequests && !isFetching,
      plans,
    }),
    [initiatedAllRequests, isFetching, plans]
  )

  useEffect(() => {
    if (!data) return
    if (
      !initiatedAllRequests &&
      !isFetching &&
      !isFetchingNextPage &&
      shouldFetchNextPage(data)
    ) {
      fetchNextPage()
    }
  }, [
    initiatedAllRequests,
    isFetchingNextPage,
    isFetching,
    data,
    shouldFetchNextPage,
    fetchNextPage,
  ])

  return resultsObject
}
